import {computed, defineComponent, type PropType, ref, watch} from 'vue';
import {AthumiService, type InvoiceSettings, type OperationsActorServiceInfo} from "@/api/enterprise-gateway-api.proxy.ts";
import {editActorMailsModal} from "@/app/admin/components/aeg-edit-actor-mails/aeg-edit-actor-mails.ts";
import {useModalEventsUtil} from "@/app/shared/helpers/modal-events-util.ts";

export default defineComponent({
    name: 'AegActorInfo',
    props: {
        actorServiceInfo: {
            type: Object as PropType<OperationsActorServiceInfo | undefined>,
            required: false,
            default: undefined
        },
        invoiceSettings: {
            type: Array as PropType<InvoiceSettings[] | undefined>,
            required: false,
            default: undefined
        },
        isCitizen: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    emits: ['edit-invoice-settings', 'invoice-settings-saved'],
    setup: function (props, { emit }) {
        const actorInfo = ref<OperationsActorServiceInfo | undefined>();
        const emails = ref<string>('');
        const modalId = ref<string>(`${editActorMailsModal}_${crypto.randomUUID()}`);

        const isKlip = computed<boolean>(() => {
            return actorInfo.value?.service === AthumiService.Klip;
        });
        
        const formatAmount = (amount: number | undefined): string => {
            if (amount === undefined || amount === null) return '0,00';
            return amount.toFixed(2).replace('.', ',');
        };
        
        const getModalId = (invoiceSettingsId: string): string => {
            return `edit-invoice-settings-${invoiceSettingsId}`;
        };

        const editActorEmails = () => {
            const modalEventsUtil = useModalEventsUtil(modalId.value);
            modalEventsUtil.triggerModalToggle();
        };
        
        const editSettings = (settings: InvoiceSettings) => {
            const settingsModalId = getModalId(settings.invoiceSettingsId!);
            const modalEventsUtil = useModalEventsUtil(settingsModalId);
            modalEventsUtil.triggerModalToggle();
        };
        
        const handleInvoiceSettingsSaved = (updatedSettings: InvoiceSettings) => {
            emit('invoice-settings-saved', updatedSettings);
        };

        const updateActorEmails = (actorEmails: {emails: string[]}) => {
            emails.value = actorEmails.emails.join(', ') || '';
        };

        const actorChanged = async (newActorServiceInfo: OperationsActorServiceInfo | undefined) => {
            actorInfo.value = newActorServiceInfo;
            emails.value = newActorServiceInfo?.emails?.join(', ') || '';
        };
        
        watch(() => props.actorServiceInfo, actorChanged, {immediate: true, deep: true});

        return {
            actorInfo,
            modalId,
            isKlip,
            isCitizen: props.isCitizen,
            invoiceSettings: props.invoiceSettings,
            emails,
            formatAmount,
            getModalId,
            editActorEmails,
            editSettings,
            handleInvoiceSettingsSaved,
            updateActorEmails
        }
    }
});
